import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AttachmentContentService {
  private readonly http = inject(HttpClient);

  fetchBlob(downloadUrl: string): Observable<Blob> {
    return this.http.get(this.resolveUrl(downloadUrl), { responseType: 'blob' });
  }

  resolveContentType(item: { fileName: string; contentType?: string }, blob: Blob): string {
    const candidates = [item.contentType, blob.type];
    for (const candidate of candidates) {
      const normalized = this.normalizeContentType(candidate);
      if (normalized && normalized !== 'application/octet-stream') {
        return normalized;
      }
    }

    const fileName = item.fileName.toLowerCase();
    if (fileName.endsWith('.pdf')) {
      return 'application/pdf';
    }
    if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(fileName)) {
      return 'image/*';
    }
    if (/\.(txt|csv|json|xml|md|log)$/.test(fileName)) {
      return 'text/plain';
    }

    return this.normalizeContentType(blob.type) || 'application/octet-stream';
  }

  isPdf(contentType: string, fileName: string): boolean {
    return contentType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');
  }

  isImage(contentType: string): boolean {
    return contentType.startsWith('image/');
  }

  isText(contentType: string): boolean {
    return contentType.startsWith('text/');
  }

  canPreviewInline(contentType: string, fileName = ''): boolean {
    return this.isPdf(contentType, fileName)
      || this.isImage(contentType)
      || this.isText(contentType);
  }

  toPreviewBlob(blob: Blob, contentType: string, fileName: string): Blob {
    if (blob.type && blob.type !== 'application/octet-stream') {
      return blob;
    }

    if (this.isPdf(contentType, fileName)) {
      return new Blob([blob], { type: 'application/pdf' });
    }
    if (this.isImage(contentType)) {
      return new Blob([blob], { type: contentType });
    }
    if (this.isText(contentType)) {
      return new Blob([blob], { type: contentType });
    }

    return blob;
  }

  normalizeContentType(value?: string): string {
    const raw = (value ?? '').trim().toLowerCase();
    if (!raw) {
      return '';
    }

    return raw.split(';')[0]?.trim() ?? '';
  }

  resolveUrl(downloadUrl: string): string {
    if (downloadUrl.startsWith('http://') || downloadUrl.startsWith('https://')) {
      return downloadUrl;
    }

    const normalized = downloadUrl.startsWith('/') ? downloadUrl : `/${downloadUrl}`;
    return `${environment.apiBaseUrl}${normalized}`;
  }

  downloadBlob(blob: Blob, fileName: string): void {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName || 'attachment';
    anchor.click();
    URL.revokeObjectURL(objectUrl);
  }
}
